/**
 ShapeDiver 3D Viewer Application

 TextureLoader.js

 Content:
   Loader used to load images, including SVG's and create THREE textures from them.

  Copyright (c) 2018 ShapeDiver GmbH

  @module TextureLoader
 */

const AXIOS = require('../../externals/axios'),
      THREE = require('../../externals/three'),
      EXPOTHREE = require('../../externals/expo-three'),
      EXPOASSETUTILS = require('../../externals/expo-asset-utils'),
      ATOBTOA = require('../util/atobtoa');

// #SS-977 clean up the Promises in here

/**
 * Global texture cache, mapping urls to texture objects
 * @private
 * @type {Object}
 */
var textureCache = {};

var TextureLoader = function() {
  var that = this;
  this.sessionDataRequest = () => { return null; };

  /**
 * Load data from given url and create THREE.Texture object
 * @param  {String} url The url to load the image data from. Can be a data URI.
 * @param {Object} options Options for setting up the new texture
 * @param  {Object} requestHeaders        Additional headers to use for requests to backend.
 * @return {Promise<THREE.Texture>}     A promise resolving to a texture object.
*/
  this.loadTexture = function(url, options, requestHeaders) {

    // Safari needs us to load the texture twice for some weird reason
    let isSafari = window &&
      window.navigator &&
      window.navigator.userAgent.indexOf('Safari') !== -1 &&
      window.navigator.userAgent.indexOf('Chrome') === -1;
    if (isSafari) {
      return _loadTexture(url, options, requestHeaders)
        .then(
          function() {
            return _loadTexture(url, options, requestHeaders);
          }
        )
      ;
    } else {
      return _loadTexture(url, options, requestHeaders);
    }
  };

  /**
   * Private function doing the actual texture loading
   * @ignore
  */
  let _loadTexture = function(url, options, requestHeaders) {
    let scope = 'TextureLoader._loadTexture';

    options = options || {};
    requestHeaders = requestHeaders || {};

    // clone a cached texture and apply options to it
    let applyOptions = function(tex) {

      tex.wrapS = [0,1,2].includes(options.wrapS) ? 1000 + options.wrapS : THREE.RepeatWrapping;
      tex.wrapT = [0,1,2].includes(options.wrapT) ? 1000 + options.wrapT : THREE.RepeatWrapping;

      let offset = options.offset || [0,0];
      tex.offset = new THREE.Vector2(offset[0], offset[1]);

      let repeat = options.repeat || [1,1];
      tex.repeat = new THREE.Vector2(repeat[0], repeat[1]);

      tex.rotation = options.rotation || 0;

      let center = options.center || [0,0];
      tex.center = new THREE.Vector2(center[0], center[1]);

      tex.uvTransform = new THREE.Matrix3();
      tex.uvTransform
        .identity()
        .translate(-tex.center.x, -tex.center.y)
        .rotate(tex.rotation)
        .scale(tex.repeat.x, tex.repeat.y)
        .translate(tex.center.x, tex.center.y)
        .translate(tex.offset.x, tex.offset.y);

      tex.needsUpdate = true;

      return tex;
    };

    // check if we got a canvas object instead of an url
    if (typeof url === 'object' && url !== null && url.nodeType === 1 && typeof url.nodeName === 'string' && url.nodeName.toUpperCase() === 'CANVAS')
    {
      let canvasTexture = new THREE.CanvasTexture(url);

      // set a callback for setting flag needsUpdate on the THREE.Texture
      if ( typeof url._addTexture !== 'function' ) {
        (function() {
          let _textures = [];
          url._addTexture = (t) => (_textures.push(t));
          Object.defineProperty(url, 'needsUpdate', {
            set: (v) => (v && _textures.forEach((t) => (t.needsUpdate = true)))
          });
        }());
      }
      url._addTexture(canvasTexture);

      return Promise.resolve(applyOptions(canvasTexture));
    }

    if (typeof(url) !== 'string') {
      return Promise.reject(new Error('Input is not a string'));
    }

    let hash = '_' + require('../util/stringChecksum')(url);
    if (textureCache.hasOwnProperty(hash)) {
      return textureCache[hash]
        .then(
          function(texture) {
            // we have to do a workaround here in case of using EXPO, because
            // of a bug in Material.clone() which does not copy the expo-three custom isDataTexture property
            let clonedTexture = texture.clone();
            if (EXPOTHREE && EXPOASSETUTILS) {
              let isDataTexture = texture.isDataTexture;
              if (isDataTexture && !clonedTexture.isDataTexture) {
                clonedTexture.isDataTexture = true;
              }
            }
            return applyOptions(clonedTexture);
          }
        )
      ;
    }

    let contentType = null;
    let content = null;
    let isBase64 = null;
    let isDataURI = null;

    textureCache[hash] = Promise.resolve()
      .then(
        function() {
          // figure out if we have a data-url, and collect information
          if (url.substring(0, 5) == 'data:') {
            // split up the url into the relevant parts and continue
            isDataURI = true;
            contentType = url.substring(url.indexOf(':') + 1, url.indexOf(';'));
            isBase64 = url.indexOf('base64') !== -1;
            content = url.substring(url.indexOf(',') + 1);
            return Promise.resolve();
          }

          // url is not a data url, download the texture
          let href = url;

          if( !url.startsWith('blob:') && 
              !url.match(new RegExp("shapediver\.com\/session\/.*\/texture\/", "i")) && 
              !url.match(new RegExp("shapediver\.com\/cdn-asset-textures\/", "i"))
          ) {
            const session = that.sessionDataRequest();
            if(session) 
              href = `${session.modelViewUrl}/api/v2/session/${session.sessionID}/image?url=${btoa(url)}`
          }

          isDataURI = false;
          let config = {
            method: 'GET',
            url: href,
            headers: {},
          };
          if (requestHeaders.authorization) {
            config.headers['Authorization'] = requestHeaders.authorization;
          }



          let success = (response) => {
            contentType = response.headers['content-type'];
            if (contentType.indexOf(';') > -1)
              contentType = contentType.substr(0, contentType.indexOf(';'));
            content = response.data;
            isBase64 = false;
            return Promise.resolve();
          };

          let counter_retries = 0;
          const max_retries = 3;
      
          let error = (error) => {
            if ((error.response && 
              error.response.status && 
              error.response.status === 502) ||
              error.request) {
              counter_retries++;
              if(counter_retries <= max_retries) return request(config);
            }
            
            error.scope = scope;
            if (error.response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              error.msg = 'Loading texture failed with HTTP status code ' + error.response.status;
            } else if (error.request) {
              // The request was made but no response was received
              // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
              // http.ClientRequest in node.js
              error.msg = 'Loading texture failed, no response from the server';
            } else {
              // Something happened in setting up the request that triggered an Error
              error.msg = 'Loading texture failed, unknown error';
            }
            return Promise.reject(error);
          };

          let request = (config) => {
            return AXIOS(config)
              .then(success, error);
          }
      
          return request(config);
        }
      )
      .then( // from here we have to differentiate between bitmap images and SVG
        function() {

          let i, j, k;

          // simple case: we can download a bitmap
          const bitmapContentTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];
          if (bitmapContentTypes.includes(contentType)) {
            return new Promise(
              function(resolve, reject) {

                if (EXPOTHREE && EXPOASSETUTILS) {

                  // get file extension from content type
                  let extension = contentType.split('/')[1].replace('e','');
                  let opt = {fileName: 'H' + hash.toString() + '.' + extension};
                  EXPOASSETUTILS.resolveAsync(url, opt)
                    .then(
                      function(localAsset) {
                        EXPOTHREE.loadTextureAsync({asset: localAsset})
                          .then(
                            function(texture) {
                              texture.minFilter = THREE.LinearFilter;
                              resolve(texture);
                            },
                            function(error) {
                              error.scope = scope;
                              error.msg = 'EXPOTHREE.loadAsync failed';
                              reject(error);
                            }
                          )
                        ;
                      },
                      function(error) {
                        error.scope = scope;
                        error.msg = 'EXPOASSETUTILS.resolveAsync failed';
                        reject(error);
                      }
                    )
                  ;
                }
                else {
                  let loader = new THREE.TextureLoader();
                  loader.load(url,
                    /* onLoad — Will be called when load completes. The argument will be the loaded texture.*/
                    function(texture) {
                      resolve(texture);
                    },
                    /* onProgress — Will be called while load progresses.
                       The argument will be the XMLHttpRequest instance, which contains .total and .loaded bytes. */
                    function(/*request*/) {
                    },
                    /* onError — Will be called when load errors. */
                    function(error) {
                      error.scope = scope;
                      error.msg = 'Texture file loading failed';
                      reject(error);
                    }
                  );
                }
              }
            );
          }
          else if (contentType === 'image/svg+xml') {

            // We can't support SVG textures in IE11 at the moment
            // #SS-977 Mathias insert the link explaining the problem
            let isIE11 = window &&
              window.navigator &&
              window.navigator.userAgent &&
              window.navigator.userAgent.indexOf('Trident/') > 0;
            if (isIE11) {
              let error = new Error('SVG textures not supported by Internet Explorer 11. Skipping the texture.');
              error.scope = scope;
              return Promise.reject(error);
            }

            // #SS-977 most of the code below is not going to work outside of a browser

            var strSVGmodified = null;
            if (isDataURI) {
              if (isBase64) {
                // strSVGmodified = window.atob(content);
                strSVGmodified = ATOBTOA.atobUTF8(content);
                // console.log(strSVGmodified);
              } else {
                strSVGmodified = content;
                strSVGmodified = (' ' + strSVGmodified).slice(1);
              }
            } else {
              strSVGmodified = content;
            }

            // create a div and put the whole svg code in there
            var svgC = document.createElement('DIV');
            svgC.id = 'svgc';
            svgC.innerHTML = strSVGmodified;

            // now we can access the svg element as a DOM object
            var svgE = svgC.getElementsByTagName('svg');
            var childImageURIs = [];
            var styleURIs = [];

            // collect image urls
            for (i = 0; i < svgE.length; ++i) {
              for (j = 0; j < 2; ++j) {
                var childImages = svgE[i].getElementsByTagName(['image', 'img'][j]);
                for (k = 0; k < childImages.length; ++k) {
                  if (childImages[k].href.baseVal.substring(0, 5) != 'data:') {
                    childImageURIs.push(childImages[k].href.baseVal);
                  }
                }
              }

              // collect potential font definitions
              // we assume styles are imported using the following syntax:
              // @import url(CSS_URL);
              var styleElements = svgE[i].getElementsByTagName('style');
              for (j = 0; j < styleElements.length; ++j) {
                var regex = /@import\x20url\(\s*(.*?)\s*\);/g;
                let m;
                while ((m = regex.exec(styleElements[j].innerHTML)) !== null) {
                  styleURIs.push(m[1]);
                }
                // make unique
                styleURIs = styleURIs.filter(
                  function(value, index, self) {
                    return self.indexOf(value) === index;
                  }
                );
              }
            }

            // creating a promise for each image which needs to be converted to a data URI
            var replacementPromises = [];
            var newP;
            var createImagePromise = function(n) {
              return new Promise(function(resolve, reject) {
                if (childImageURIs[n].length > 0) {
                  require('../util/getImageBase64')(childImageURIs[n])
                    .then(
                      function(response) {
                        // compose data url
                        var imgDataUrl = 'data:' + response.contentType + ';base64,' + response.data;

                        // replace url in SVG string
                        // CAUTION theoretically this could cause unwanted replacements
                        strSVGmodified = strSVGmodified.replace(childImageURIs[n], imgDataUrl);
                        resolve();
                      }
                    )
                    .catch(
                      function(error) {
                        reject(error);
                      }
                    )
                  ;
                } else {
                  resolve();
                }
              });
            };

            for (i = 0; i < childImageURIs.length; ++i) {
              newP = createImagePromise(i);
              replacementPromises.push(newP);
            }

            // now we create promises for the google fonts to be imported
            var createStylePromise = function(styleUrl) {
              return new Promise(
                function(resolve, reject) {
                  AXIOS.get(styleUrl)
                    .then(
                      function(response) {
                        return response.data;
                      }
                    )
                    .then(
                      function(cssString) {
                        // we assume that fonts are imported using the following syntax:
                        // url(FONT_URI);
                        var fontURLs = [];
                        var regex = /url\(\s*(.*?)\s*\)/g;
                        let m;
                        while ((m = regex.exec(cssString)) !== null) {
                          fontURLs.push(m[1]);
                        }

                        var fontPromises = [];
                        var createFontPromise = function(fUrl) {
                          return new Promise(
                            function(resolveN, rejectN) {
                              require('../util/getImageBase64')(fUrl)
                                .then(
                                  function(response) {
                                    // compose data url
                                    var fontDataUrl = 'data:' + response.contentType + ';base64,' + response.data;
                                    if (fUrl.length > 0) {
                                      cssString = cssString.replace(fUrl, fontDataUrl);
                                    }
                                    resolveN();
                                  }
                                )
                                .catch(
                                  function() {
                                    rejectN(new Error('Error converting ' + fUrl + ' to base64'));
                                  }
                                )
                              ;
                            }
                          );
                        };
                        for (j = 0; j < fontURLs.length; ++j)
                          fontPromises.push(createFontPromise(fontURLs[j]));

                        return Promise.all(fontPromises)
                          .then(
                            function() {
                              return cssString;
                            }
                          )
                          .then(
                            function(cssStringModified) {
                              if (url.length > 0) {
                                strSVGmodified = strSVGmodified.replace('@import url(' + styleUrl + ');', cssStringModified);
                              }
                              resolve();
                            }
                          )
                          .catch(
                            function() {
                              reject(new Error('Error converting CSS string.'));
                            }
                          )
                        ;
                      }
                    )
                  ;
                }
              );
            };
            for (i = 0; i < styleURIs.length; ++i) {
              newP = createStylePromise(styleURIs[i]);
              replacementPromises.push(newP);
            }

            // create texture once all images have been converted
            return Promise.all(replacementPromises)
              .then(
                function() {
                  return new Promise(
                    function(resolve, reject) {
                      var du = 'data:image/svg+xml,' + encodeURIComponent(strSVGmodified);
                      var canvas = document.createElement('canvas');
                      // canvas size 8192 is very high for normal applications, needs to be configurable, lowered to 2048 for now
                      // see SS-25
                      let resolution = 2048;
                      canvas.width = resolution;
                      canvas.height = resolution;
                      var ctx = canvas.getContext('2d');
                      var img = new Image(); // same as document.createElement('img')
                      img.onload = function() {
                        ctx.drawImage(img, 0, 0, resolution, resolution);
                        var texture = new THREE.Texture(canvas);
                        resolve(texture);
                      };
                      img.onerror = function(e) {
                        reject(e);
                      };
                      img.crossOrigin = 'Anonymous';
                      img.src = du;
                    }
                  );
                }
              )
            ;
          }
          else { // unknown content-type
            return Promise.reject(new Error('Unkonwn content-type ' + contentType + ' when downloading ' + url));
          }
        }
      )
      // .catch(
      //   function(err) {
      //     if (err) that.warn(err);
      //   }
      // )
    ;

    return textureCache[hash]
      .then(
        function(texture) {
          // we have to do a workaround here in case of using EXPO, because
          // of a bug in Material.clone() which does not copy the expo-three custom isDataTexture property
          let clonedTexture = texture.clone();
          if (EXPOTHREE && EXPOASSETUTILS) {
            let isDataTexture = texture.isDataTexture;
            if (isDataTexture && !clonedTexture.isDataTexture) {
              clonedTexture.isDataTexture = true;
            }
          }
          return applyOptions(clonedTexture);
        }
      )
    ;
  };
};

var TextureLoaderSingleton = new TextureLoader();

module.exports = TextureLoaderSingleton;
